// Suffix-based unit detection for SQL column aliases.
//
// The system prompt requires Claude to alias aggregate columns with a
// deterministic suffix so downstream code can format them:
//   *_amount / *_revenue / *_cost / *_price / *_salary / *_budget → currency
//   *_percent / *_percentage / *_pct / *_rate                     → percent
//   *_count                                                       → integer
//
// We pattern-match on the column name and return a tiny units descriptor
// used by Table.tsx (cell formatter), valueAxisStyle (axis labels), and
// axisTooltip (per-series values). No semantic-model lookup needed — the
// alias *is* the contract.

export interface ColumnUnits {
  prefix?: string;
  postfix?: string;
  /** Force integer display (no decimals). Default behaviour preserves up to 2 decimals. */
  integer?: boolean;
}

const CURRENCY_SUFFIXES = [
  "_amount",
  "_revenue",
  "_cost",
  "_price",
  "_salary",
  "_budget",
  "_ar",
  "_collected",
  "_billed",
  "_denied",
];
const PERCENT_SUFFIXES = ["_percent", "_percentage", "_pct", "_rate"];
const COUNT_SUFFIXES = ["_count"];

/** Best-effort units inferred from a column alias. Returns null when no
 *  signal — leaves the value rendered as a plain number. */
export function unitsFromColumnName(col: string | null | undefined): ColumnUnits | null {
  if (!col) return null;
  const lower = col.toLowerCase();
  if (CURRENCY_SUFFIXES.some((s) => lower.endsWith(s))) {
    return { prefix: "$" };
  }
  if (PERCENT_SUFFIXES.some((s) => lower.endsWith(s))) {
    return { postfix: "%" };
  }
  if (COUNT_SUFFIXES.some((s) => lower.endsWith(s))) {
    return { integer: true };
  }
  return null;
}

/** Format a single number with thousands separators + optional prefix/postfix.
 *  Used for table cells and tooltip values where space allows full precision. */
export function formatWithUnits(value: unknown, units: ColumnUnits | null): string {
  if (value == null || value === "") return "";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  const integer = units?.integer ?? Number.isInteger(n);
  const opts: Intl.NumberFormatOptions = integer
    ? { maximumFractionDigits: 0 }
    : { minimumFractionDigits: 0, maximumFractionDigits: 2 };
  const body = new Intl.NumberFormat("en-US", opts).format(n);
  return `${units?.prefix ?? ""}${body}${units?.postfix ?? ""}`;
}

/** Compact format ("1.2M", "350K") with optional prefix/postfix. Used by
 *  value-axis labels where horizontal real estate matters. Mirrors the
 *  existing `compactNumber` shape so callers can swap drop-in. */
export function formatCompactWithUnits(value: unknown, units: ColumnUnits | null): string {
  if (value == null || value === "") return "";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  const abs = Math.abs(n);
  let body: string;
  if (abs >= 1e9) body = trim(n / 1e9) + "B";
  else if (abs >= 1e6) body = trim(n / 1e6) + "M";
  else if (abs >= 1e4) body = trim(n / 1e3) + "K";
  else body = n.toLocaleString();
  return `${units?.prefix ?? ""}${body}${units?.postfix ?? ""}`;
}

function trim(n: number): string {
  if (Math.abs(n) >= 10) return Math.round(n).toString();
  const s = n.toFixed(1);
  return s.endsWith(".0") ? s.slice(0, -2) : s;
}
